// タスク詳細 Notes に添付するドキュメントファイルの検証・パス判定ヘルパ。
// 保存形式や制約は docs/superpowers/specs/2026-07-30-task-notes-file-attach-design.md を参照。
// 画像は lib/task-image.ts、パス生成 (buildTaskImagePath) も同ファイルを共用する。

export const TASK_FILE_MAX_BYTES = 10 * 1024 * 1024; // 10MB

// 拡張子 → ストレージ upload 時に contentType へ明示する正規 MIME
export const TASK_FILE_EXT_TO_MIME: Record<string, string> = {
  pdf: "application/pdf",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  txt: "text/plain",
  md: "text/markdown",
  csv: "text/csv",
};

// ブラウザが報告する MIME → 拡張子（エイリアス含む）。
// File.type は OS 依存で空文字や別名になることがあるため、ここに無ければ拡張子で判定する。
const TASK_FILE_MIME_TO_EXT: Record<string, string> = {
  "application/pdf": "pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "pptx",
  "text/plain": "txt",
  "text/markdown": "md",
  "text/x-markdown": "md",
  "text/csv": "csv",
  "application/csv": "csv",
};

/** <input type="file" accept> 用の拡張子リスト。 */
export const TASK_FILE_ACCEPT = Object.keys(TASK_FILE_EXT_TO_MIME)
  .map((ext) => `.${ext}`)
  .join(",");

/** MIME 優先・拡張子フォールバックで対応拡張子を判定する。非対応は null。 */
export function resolveTaskFileExt(file: { name: string; type: string }): string | null {
  const byMime = TASK_FILE_MIME_TO_EXT[file.type];
  if (byMime) return byMime;
  const m = /\.([a-z0-9]+)$/i.exec(file.name);
  const ext = m?.[1]?.toLowerCase();
  return ext && ext in TASK_FILE_EXT_TO_MIME ? ext : null;
}

/** 形式だけの判定（paste/drop のファイル振り分け用。サイズ超過は validate 側で toast を出す）。 */
export function isAcceptedTaskFile(file: { name: string; type: string }): boolean {
  return resolveTaskFileExt(file) !== null;
}

export type TaskFileValidation =
  | { ok: true; ext: string; contentType: string }
  | { ok: false; reason: "type" | "size" };

export function validateTaskFileFile(file: {
  name: string;
  type: string;
  size: number;
}): TaskFileValidation {
  const ext = resolveTaskFileExt(file);
  if (!ext) return { ok: false, reason: "type" };
  if (file.size > TASK_FILE_MAX_BYTES) return { ok: false, reason: "size" };
  return { ok: true, ext, contentType: TASK_FILE_EXT_TO_MIME[ext] };
}

// <notebookId>/<lineId>/<uuid>.<対応拡張子> 形式。
// ファイル名部分の uuid は buildTaskImagePath (crypto.randomUUID) が必ず生成するため、
// 通常リンク・外部 URL との誤判定はここで弾ける（http(s):// は `//` の空セグメントで不一致）。
const ATTACHMENT_PATH_RE = new RegExp(
  `^[^/\\s]+/[^/\\s]+/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\\.(${Object.keys(TASK_FILE_EXT_TO_MIME).join("|")})$`,
  "i",
);

/** href がドキュメント添付のストレージパスかどうか（リンク → taskFile ノード変換の判定に使う）。 */
export function isTaskAttachmentPath(href: string): boolean {
  return ATTACHMENT_PATH_RE.test(href);
}
